import React, { useEffect, useState } from 'react'
import omit from 'lodash/omit'

import ApplicationForm from './form'
import ApplicationTable from './table'

import { useStateValue } from 'context/store'
import { useApiRequest } from 'utils/customHooks/useApiRequest'
import { getFromApi, postToApi, putToApi } from 'actions'
import { OPERACIONES_APLICACIONES, VALUES } from 'utils/constants'
import { buildFiltersDescriptors } from '../../commons/SearchFilters'
import SecurityAdministrationLayout from '../../commons/SecurityAdministrationLayout'
import { useHasOperation } from 'hooks/useHasOperation'

const INITIAL_APPLICATION_FORM_STATE = {
   id: '',
   name: '',
   type: '',
   link: '',
   subdomainId: '',
   status: '',
   file: '',
   mapa: ''
}

const { STATUS_OPTIONS, APPLICATION_TYPE_OPTIONS } = VALUES

const TITLE = {
   [VALUES.FORM_BEHAVIOR.REGISTER]: 'Registrar Aplicación',
   [VALUES.FORM_BEHAVIOR.SEARCH]: 'Consultar Aplicaciones',
   [VALUES.FORM_BEHAVIOR.EDIT]: 'Editar Aplicación'
}

// TODO: llevar al utils
function validateUrlResources(url) {
   return url[url.length - 1] === '/' ? url.substring(0, url.length - 1) : url
}

function Application({ isSearch }) {
   const [isShowingPopover, setPopoverProp] = useState(true)
   const [allActiveSubdomains, setAllActiveSubdomains] = useState([])
   const [options, setOptions] = useState([...STATUS_OPTIONS])

   const [formData, setFormData] = useState(INITIAL_APPLICATION_FORM_STATE)
   const [formBehavior, setFormBehavior] = useState(
      isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER
   )
   const [tableData, setTableData] = useState(VALUES.TABLE_DATA_INTIAL_STATE)
   const [paginationParams, setPaginationParams] = useState(VALUES.PAGING_ORDER_INITIAL_STATE)
   const [searchFilters, setSearchFilters] = useState({})

   const [_, dispatch] = useStateValue()

   const [showError, setShowError] = useState(false)
   const [textError, setTextError] = useState('')
   const [confirmAlertStatus, setConfirmAlertStatus] = useState(false)

   const EDITAR_APLICACION = useHasOperation(OPERACIONES_APLICACIONES.EDITAR_APLICACION)

   // ---------------- Clean Form -----------------
   function cleanForm() {
      setFormData({ ...INITIAL_APPLICATION_FORM_STATE })
      setFormBehavior(isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER)
   }

   function clean() {
      setSearchFilters({})
      setTableData(VALUES.TABLE_DATA_INTIAL_STATE)
      setPopoverProp(true)
      setPaginationParams(VALUES.PAGING_ORDER_INITIAL_STATE)
      cleanForm()
   }

   useEffect(() => {
      clean()
   }, [isSearch]) // eslint-disable-line react-hooks/exhaustive-deps

   // ------------- Fetch all Initial Active Subdomains  -----------------
   async function _getAllActiveSubdomains() {
      const url = `${process.env.REACT_APP_SUBDOMAIN_API}/subdomains/active`
      const searchedSubdomains = await getFromApi(url)
      setAllActiveSubdomains(searchedSubdomains)
      const subdomainOptions = searchedSubdomains.map(subdomain => ({
         label: subdomain.name,
         value: subdomain.id
      }))
      setOptions(prev => [...prev, ...subdomainOptions])
   }

   const getAllActiveSubdomains = useApiRequest(_getAllActiveSubdomains, 'Aplicaciones: Consultar Subdominios Activos')

   useEffect(() => {
      getAllActiveSubdomains()
   }, [])

   // ------------ Search Applications -----------------
   function getFiltersDescriptors(filters) {
      const { type, ...otherFilters } = filters
      const descriptors = buildFiltersDescriptors(options)(otherFilters) || []
      if (type) {
         const operationType = APPLICATION_TYPE_OPTIONS.find(appTypeOpt => appTypeOpt.value === type)
         descriptors.push(operationType)
      }
      return descriptors
   }

   async function removeSearchFilter(filter) {
      let remainingFilters = omit(searchFilters, [filter.filterName])
      if (!filter.filterName) {
         remainingFilters = omit(remainingFilters, ['type'])
      }
      await searchApplications(remainingFilters, VALUES.PAGING_ORDER_INITIAL_STATE)
   }

   async function _searchApplications(externalFilters, pagingFromTableParams) {
      const filters = externalFilters ? externalFilters : searchFilters
      const paginationFilters = {
         ...paginationParams,
         ...pagingFromTableParams
      }
      setSearchFilters(filters)
      setPaginationParams(paginationFilters)
      const data = {}
      if (filters.name) {
         data.name = filters.name
      }
      if (filters.type) {
         data.type = filters.type
      }
      if (filters.subdomainId) {
         data.subdomainId = filters.subdomainId
      }
      if (filters.status) {
         data.status = filters.status
      }
      const payload = { ...data, ...paginationFilters }
      const url = `${process.env.REACT_APP_APPLICATIONS_API}/applications/search`
      const searchedApplications = await getFromApi(url, payload)
      setTableData(searchedApplications)
      setFormData(INITIAL_APPLICATION_FORM_STATE)
   }

   const searchApplications = useApiRequest(_searchApplications, 'Aplicaciones: Consultar')

   // --------------- Register Applications -----------------------
   async function _registerApplication() {
      const payload = new FormData()
      payload.append('name', formData.name)
      payload.append('type', formData.type)
      payload.append('link', formData.link)
      payload.append('resource', validateUrlResources(formData.resource))
      payload.append('subdomainId', formData.subdomainId)
      payload.append('status', formData.status)
      payload.append('file', formData.file)

      const url = `${process.env.REACT_APP_APPLICATIONS_API}/applications`
      const registeredApplication = await postToApi(url, payload)
      setFormData(INITIAL_APPLICATION_FORM_STATE)
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [registeredApplication]
      })
      dispatch({
         type: 'OPEN_SNACKBAR',
         openMessage: {
            message: `Se guardó correctamente la Aplicación ${registeredApplication.name}`,
            severity: 'info'
         }
      })
   }

   const registerApplication = useApiRequest(_registerApplication, 'Aplicaciones: Registrar')

   // -------------- Edit Applications ----------------
   async function _editApplication() {
      /* const payload = {
            name: formData.name,
            subdomainId: formData.subdomainId,
            type: formData.type,
            link: formData.link,
            resource: validateUrlResources(formData.resource),
            status: formData.status
        } */

      const payload = new FormData()
      payload.append('name', formData.name)
      payload.append('type', formData.type)
      payload.append('link', formData.link)
      payload.append('resource', validateUrlResources(formData.resource))
      payload.append('subdomainId', formData.subdomainId)
      payload.append('status', formData.status)

      if (typeof formData.file === 'object') {
         payload.append('file', formData.file)
      }

      const url = `${process.env.REACT_APP_APPLICATIONS_API}/applications/${formData.id}`
      const editedApplication = await putToApi(url, payload)
      const formBehavior = isSearch ? VALUES.FORM_BEHAVIOR.SEARCH : VALUES.FORM_BEHAVIOR.REGISTER
      setFormBehavior(formBehavior)
      setFormData(INITIAL_APPLICATION_FORM_STATE)
      setTableData({
         ...VALUES.TABLE_DATA_INTIAL_STATE,
         data: [editedApplication]
      })
      dispatch({
         type: 'OPEN_SNACKBAR',
         openMessage: {
            message: `Se actualizó correctamente la Aplicación ${editedApplication.name}`,
            severity: 'info'
         }
      })
   }

   const editApplication = useApiRequest(_editApplication, 'Aplicaciones: Editar')

   // -------------- Form Actions ---------------
   function actionSubmit(type) {
      setPopoverProp(false)
      if (type === VALUES.FORM_BEHAVIOR.REGISTER) {
         registerApplication()
      }
      if (type === VALUES.FORM_BEHAVIOR.EDIT) {
         editApplication()
      }
      if (type === VALUES.FORM_BEHAVIOR.SEARCH) {
         searchApplications(formData, VALUES.PAGING_ORDER_INITIAL_STATE)
      }
   }

   function actionEdit(rowData) {
      setFormBehavior(VALUES.FORM_BEHAVIOR.EDIT)
      setPopoverProp(true)
      setFormData({
         id: rowData.id,
         name: rowData.name,
         type: rowData.type,
         link: rowData.link,
         resource: rowData.resource,
         subdomainId: rowData.subdomain.id,
         status: rowData.status,
         status: rowData.status,
         file: rowData.mutualWorld ? rowData.mutualWorld.image : 'Cargar icono Mundo Mutual*'
      })
   }

   const handleChangeAlert = () => {
      setAlertStatus(true)
   }

   const alertClose = () => {
      setShowError(false)
   }

   const alertOpen = () => {
      setShowError(true)
   }

   const actions = []
   if (EDITAR_APLICACION) {
      actions.push({
         icon: 'edit',
         tooltip: 'Editar aplicacion',
         onClick: (_, rowData) => actionEdit(rowData)
      })
   }

   return (
      <SecurityAdministrationLayout
         alertClose={alertClose}
         showError={showError}
         textError={textError}
         confirmAlertStatus={confirmAlertStatus}
         title={TITLE[formBehavior]}
         form={
            <ApplicationForm
               formData={formData}
               setForm={setFormData}
               actionSubmit={actionSubmit}
               cleanCallback={cleanForm}
               formBehavior={formBehavior}
               subdomains={allActiveSubdomains}
               handleChangeAlert={handleChangeAlert}
               alertOpen={alertOpen}
               setShowError={setShowError}
               setTextError={setTextError}
            />
         }
         table={
            <ApplicationTable
               isSearch={isSearch}
               order={paginationParams.order}
               applications={tableData}
               actionSubmit={actionSubmit}
               searchApplications={searchApplications}
               customActions={actions}
               actionEdit={actionEdit}
               cleanCallback={clean}
               setShowError={setShowError}
               setTextError={setTextError}
               setConfirmAlertStatus={setConfirmAlertStatus}
            />
         }
         popoverProp={isShowingPopover}
         setPopoverProp={setPopoverProp}
         searchFilters={getFiltersDescriptors(searchFilters)}
         onRemoveFilter={removeSearchFilter}
      />
   )
}

export default Application
